import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, catchError, of, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AUTH_API_URL } from '../constants/app.constants';
import {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
  LogoutRequest,
  UserProfile,
  ChangePasswordRequest,
  ChangePasswordResponse,
  VerifyTokenRequest,
  VerifyTokenResponse
} from '../models/auth.model';
import { isTokenExpired, shouldRefreshToken } from '../utils/jwt.util';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  
  private currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  // Timer for periodic token refresh
  private refreshTimer?: ReturnType<typeof setInterval>;
  private readonly REFRESH_CHECK_INTERVAL = 60 * 1000; // Check every 1 minute

  constructor() {
    // Load user from storage on init
    // Don't clear auth on error - let the app handle it naturally
    // If token is invalid, user will be redirected by guards or when making actual API calls
    const token = this.getAccessToken();
    if (token) {
      // Start token refresh timer
      this.startTokenRefreshTimer();
      
      this.getCurrentUser().subscribe({
        next: (user) => {
          this.currentUserSubject.next(user);
        },
        error: (error) => {
          // Don't clear auth in constructor - just log the error
          // The app will handle invalid tokens when making actual API calls
          // This prevents logout on F5 when there are temporary network issues
          console.warn('[AuthService] Failed to load user on init:', error);
          // Keep token - let guards or API calls handle authentication
        }
      });
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    });
  }

  // Token Management
  getAccessToken(): string | null {
    const localToken = localStorage.getItem('access_token');
    const sessionToken = sessionStorage.getItem('access_token');
    const token = localToken || sessionToken;
    
    // Removed verbose logging - this method is called too frequently
    // Uncomment for debugging if needed:
    // console.log('[AuthService] getAccessToken:', {
    //   hasLocalToken: !!localToken,
    //   hasSessionToken: !!sessionToken,
    //   tokenLength: token?.length,
    //   tokenPreview: token ? token.substring(0, 20) + '...' : null
    // });
    
    return token;
  }

  getRefreshToken(): string | null {
    const localToken = localStorage.getItem('refresh_token');
    const sessionToken = sessionStorage.getItem('refresh_token');
    const token = localToken || sessionToken;
    
    // Removed verbose logging - this method is called too frequently
    // Uncomment for debugging if needed:
    // console.log('[AuthService] getRefreshToken:', {
    //   hasLocalToken: !!localToken,
    //   hasSessionToken: !!sessionToken,
    //   tokenLength: token?.length
    // });
    
    return token;
  }

  setTokens(accessToken: string, refreshToken: string, rememberMe: boolean = false): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('access_token', accessToken);
    storage.setItem('refresh_token', refreshToken);
    
    // Restart timer after setting new tokens
    this.startTokenRefreshTimer();
  }

  clearAuth(): void {
    // Stop timer before clearing auth
    this.stopTokenRefreshTimer();
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    this.currentUserSubject.next(null);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Check if access token is expired
   */
  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      console.log('[AuthService] isAccessTokenExpired: No token found');
      return true;
    }
    const expired = isTokenExpired(token);
    console.log('[AuthService] isAccessTokenExpired:', expired);
    return expired;
  }

  /**
   * Check if token should be refreshed (at 2/3 of lifetime)
   */
  shouldRefreshAccessToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      console.log('[AuthService] shouldRefreshAccessToken: No token found');
      return false;
    }
    const shouldRefresh = shouldRefreshToken(token);
    console.log('[AuthService] shouldRefreshAccessToken:', shouldRefresh);
    return shouldRefresh;
  }

  /**
   * Start periodic token refresh timer
   * Checks every minute if token needs refresh (at 2/3 lifetime)
   */
  private startTokenRefreshTimer(): void {
    // Clear existing timer if any
    this.stopTokenRefreshTimer();
    
    console.log('[AuthService] Starting token refresh timer (check every 1 minute)');
    
    this.refreshTimer = setInterval(() => {
      const token = this.getAccessToken();
      if (!token) {
        console.log('[AuthService] Timer: No token found, stopping refresh timer');
        this.stopTokenRefreshTimer();
        return;
      }

      // Check if token needs refresh (at 2/3 lifetime)
      const expired = isTokenExpired(token);
      const shouldRefresh = shouldRefreshToken(token);

      if (expired || shouldRefresh) {
        console.log('[AuthService] Timer: Token needs refresh (expired:', expired, ', shouldRefresh:', shouldRefresh, '), refreshing now...');
        this.refreshToken().subscribe({
          next: () => {
            console.log('[AuthService] Timer: Token refreshed successfully');
          },
          error: (error) => {
            console.error('[AuthService] Timer: Refresh failed', error);
            // If refresh fails, stop timer (will be handled by logout in refreshToken)
            this.stopTokenRefreshTimer();
          }
        });
      } else {
        console.log('[AuthService] Timer: Token still valid, no refresh needed');
      }
    }, this.REFRESH_CHECK_INTERVAL);
  }

  /**
   * Stop periodic token refresh timer
   */
  private stopTokenRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
      console.log('[AuthService] Token refresh timer stopped');
    }
  }

  /**
   * Automatically refresh token if needed (at 2/3 of lifetime or expired)
   * Returns Observable that completes when token is refreshed or if no refresh is needed
   */
  refreshTokenIfNeeded(): Observable<RefreshResponse | null> {
    const token = this.getAccessToken();
    
    console.log('[AuthService] refreshTokenIfNeeded: Checking token...');
    
    // No token, nothing to refresh
    if (!token) {
      console.log('[AuthService] refreshTokenIfNeeded: No token, returning null');
      return of(null);
    }

    const expired = isTokenExpired(token);
    const shouldRefresh = shouldRefreshToken(token);

    // Token is expired or should be refreshed (at 2/3 lifetime)
    if (expired || shouldRefresh) {
      console.log('[AuthService] refreshTokenIfNeeded: Token needs refresh (expired:', expired, ', shouldRefresh:', shouldRefresh, '), calling refreshToken()');
      return this.refreshToken().pipe(
        tap((response) => {
          console.log('[AuthService] refreshTokenIfNeeded: Token refreshed successfully');
        }),
        catchError((error) => {
          console.error('[AuthService] refreshTokenIfNeeded: Refresh failed', error);
          return throwError(() => error);
        })
      );
    }

    // Token is still valid, no refresh needed
    console.log('[AuthService] refreshTokenIfNeeded: Token is still valid, no refresh needed');
    return of(null);
  }

  // API Methods
  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${AUTH_API_URL}/register`, data);
  }

  login(data: LoginRequest, includeScoped: boolean = true, rememberMe: boolean = false): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${AUTH_API_URL}/login?include_scoped=${includeScoped}`,
      data
    ).pipe(
      tap((response) => {
        this.setTokens(response.access_token, response.refresh_token, rememberMe);
        // Get user profile after login
        this.getCurrentUser().subscribe({
          next: (user) => {
            this.currentUserSubject.next(user);
          }
        });
      })
    );
  }

  refreshToken(includeScoped: boolean = true): Observable<RefreshResponse> {
    console.log('========================================');
    console.log('[AuthService] refreshToken: ===== STARTING REFRESH =====');
    console.log('[AuthService] refreshToken: includeScoped:', includeScoped);
    
    const refreshToken = this.getRefreshToken();
    console.log('[AuthService] refreshToken: Refresh token from storage:', {
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length
    });
    
    if (!refreshToken) {
      console.error('[AuthService] refreshToken: ✗ No refresh token available!');
      return new Observable(observer => {
        observer.error('No refresh token available');
      });
    }

    const refreshUrl = `${AUTH_API_URL}/refresh?include_scoped=${includeScoped}`;
    const refreshPayload = { refresh_token: refreshToken } as RefreshRequest;
    
    console.log('[AuthService] refreshToken: Calling refresh API...');
    console.log('[AuthService] refreshToken: URL:', refreshUrl);
    console.log('[AuthService] refreshToken: Payload:', {
      hasRefreshToken: !!refreshPayload.refresh_token,
      refreshTokenLength: refreshPayload.refresh_token?.length
    });

    console.log('[AuthService] refreshToken: Making HTTP POST request...');
    
    // Determine storage type before refresh (to maintain rememberMe preference)
    const rememberMe = !!localStorage.getItem('access_token');
    
    return this.http.post<RefreshResponse>(refreshUrl, refreshPayload).pipe(
      tap((response) => {
        console.log('[AuthService] refreshToken: ✓ HTTP request successful!');
        console.log('[AuthService] refreshToken: Response received:', {
          hasAccessToken: !!response.access_token,
          hasRefreshToken: !!response.refresh_token,
          accessTokenLength: response.access_token?.length,
          refreshTokenLength: response.refresh_token?.length
        });
        
        // Use setTokens to ensure timer is restarted
        const newRefreshToken = response.refresh_token || refreshToken;
        this.setTokens(response.access_token, newRefreshToken, rememberMe);
        
        console.log('[AuthService] refreshToken: ===== REFRESH COMPLETED SUCCESSFULLY =====');
      }),
      catchError((error) => {
        console.error('========================================');
        console.error('[AuthService] refreshToken: ✗ REFRESH FAILED!');
        console.error('[AuthService] refreshToken: Error status:', error?.status);
        console.error('[AuthService] refreshToken: Error statusText:', error?.statusText);
        console.error('[AuthService] refreshToken: Error message:', error?.message);
        console.error('[AuthService] refreshToken: Error details:', error);
        
        // If refresh fails, clear auth and redirect to login
        console.log('[AuthService] refreshToken: Clearing auth data...');
        this.clearAuth();
        console.log('[AuthService] refreshToken: Redirecting to login...');
        this.router.navigate(['/login']);
        console.error('[AuthService] refreshToken: ===== REFRESH FAILED - AUTH CLEARED =====');
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearAuth();
      return of(void 0);
    }

    return this.http.post<void>(
      `${AUTH_API_URL}/logout`,
      { refresh_token: refreshToken } as LogoutRequest
    ).pipe(
      tap(() => {
        this.clearAuth();
        this.router.navigate(['/login']);
      }),
      catchError(() => {
        // Even if logout fails, clear local auth
        this.clearAuth();
        this.router.navigate(['/login']);
        return of(void 0);
      })
    );
  }

  getCurrentUser(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${AUTH_API_URL}/me`, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
      })
    );
  }

  getScopedPermissions(): Observable<any> {
    return this.http.get<any>(`${AUTH_API_URL}/me/scoped`, {
      headers: this.getAuthHeaders()
    });
  }

  changePassword(data: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(
      `${AUTH_API_URL}/me/change-password`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  verifyToken(data: VerifyTokenRequest): Observable<VerifyTokenResponse> {
    return this.http.post<VerifyTokenResponse>(
      `${AUTH_API_URL}/verify`,
      data
    );
  }

  introspectToken(data: VerifyTokenRequest): Observable<any> {
    return this.http.post<any>(
      `${AUTH_API_URL}/introspect`,
      data
    );
  }
}

